"use client";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ProfileHoverCardPanel,
  computeHoverCardPosition,
  HOVER_CARD_OPEN_DELAY,
  HOVER_CARD_CLOSE_DELAY,
  type HoverCardCoords,
} from "./ProfileHoverCard";
import { useHoverCardKeyboard } from "@/lib/hooks/useHoverCardKeyboard";

interface EmojiTooltip {
  x: number;
  y: number;
  src: string;
  shortcode: string;
}

interface MentionHover {
  userId: number;
  coords: HoverCardCoords;
}

/**
 * The person a stored mention anchor is about — from `data-user-id`, which
 * `src/lib/markdown.ts` writes onto every one of them (see
 * `retargetMentionHrefs` below).
 */
/** Whether an element is, or sits inside, a mention anchor — the only thing
 *  in the cooked body that is a trigger for the card. */
function isMentionTrigger(el: Element): boolean {
  return el.closest("a.mention") != null;
}

function mentionUserId(anchor: HTMLAnchorElement): number | null {
  const raw = anchor.dataset.userId;
  if (!raw || !/^[0-9]+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * A stored mention anchor already carries the id, and this uses it.
 *
 * `src/lib/markdown.ts:113-115` writes three attributes, not two:
 *
 *     <a href="/profil/{username}" class="mention" data-user-id="{id}">@name</a>
 *
 * The sanitiser allowlist keeps `data-user-id` (`markdown.ts:316`),
 * `test/markdown.test.ts` pins it, and quirk R11 records the shape verbatim.
 * Q43 records that the ETL re-rendered every migrated post through
 * `renderMarkdown()`, so the id is on the old anchors too — verified against the
 * corpus, where all 9 stored `/profil/` anchors carry it and none lack it:
 *
 *     select count(*) from posts
 *      where body_html like '%class="mention"%'
 *        and body_html not like '%data-user-id%';   -- 0
 *
 * So the mention was never ambiguous — only the segment in its href is. The
 * anchor says `/profil/10181` and `data-user-id="181"`; `/profil/` is
 * id-addressed now (O-21a), so the href is rewritten from the attribute and the
 * link lands on user 181, who was mentioned. The previous version deleted the
 * href on the grounds that the id could not be known; it could, and deleting it
 * broke every mention in the product — a readable handle 404'd
 * (`/profil/nielsoerbaek`, with `data-user-id="191"` in the same tag) and a
 * numeric one became a non-navigating span.
 *
 * It rewrites the string, not the DOM. Walking `containerRef` in an effect and
 * calling `setAttribute("href", …)` would be wrong twice: the server-rendered
 * markup would still carry the broken href, so a reader clicking before
 * hydration (or a crawler) gets the 404; and a DOM mutation lives only as long
 * as the injected markup, which React re-applied on every state change until the
 * `cookedHtml` memo below went in — exactly what happened to the emoji `title`
 * stripping further down.
 *
 * It is a regex over HTML, normally the wrong tool. What makes it acceptable:
 * it matches only an `<a>` open tag whose class is `mention`, the only substring
 * it copies into its output is `data-user-id`'s run of digits, and it runs once
 * per body, memoised.
 *
 * Delete this when `markdown.ts` writes `/profil/${id}` as the href in the first
 * place — the rewrite is a no-op then.
 */
export function retargetMentionHrefs(html: string): string {
  return html.replace(/<a\b[^>]*>/gi, (tag) => {
    // `class` must actually contain the whole word `mention`.
    if (!/\sclass\s*=\s*"(?:[^"]*\s)?mention(?:\s[^"]*)?"/i.test(tag)) return tag;

    const id = tag.match(/\sdata-user-id\s*=\s*"([0-9]+)"/i);
    if (id) {
      const href = ` href="/profil/${id[1]}"`;
      return /\shref\s*=\s*"[^"]*"/i.test(tag)
        ? tag.replace(/\shref\s*=\s*"[^"]*"/i, href)
        : tag.replace(/^<a\b/i, `<a${href}`);
    }

    /**
     * No `data-user-id`: the corpus has none and the renderer cannot produce
     * one, so this arm backstops hand-written or externally-authored markup. A
     * numeric handle is the dangerous shape — `10181` is user 181's username
     * *and* a different real user's id, so the route cannot tell and would open
     * a stranger's profile silently; it stops being a link. A readable handle is
     * left to 404 loudly, the cost O-21 accepted for stale links.
     */
    return /\shref\s*=\s*"\/profil\/[0-9]+"/i.test(tag)
      ? tag.replace(/\shref\s*=\s*"[^"]*"/i, "")
      : tag;
  });
}

export function PostHtml({
  html,
  className,
  categoryId,
  bodyRef,
}: {
  html: string;
  className?: string;
  // Group the content is shown in — forwarded to the mention hover-card so it
  // matches the contextual profile card shown for post/comment authors.
  categoryId?: number;
  /** The cooked-body div, for a caller that needs to MEASURE it — `RichContent`
   *  asks whether its line-clamp cut anything before offering "Vis mere". It has
   *  to be this element: it is the one carrying the clamp, so a wrapper around
   *  it reports the clipped height as its own and never looks overflowing. */
  bodyRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const [tooltip, setTooltip] = useState<EmojiTooltip | null>(null);
  const [mentionHover, setMentionHover] = useState<MentionHover | null>(null);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mentionOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mentionCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The card, so the keyboard contract can hand focus into it (OSO-512).
   *  Where focus goes BACK is not a second ref: it is the mention the card is
   *  anchored to — see `restoreMentionFocus`. */
  const mentionCardRef = useRef<HTMLDivElement>(null);
  /** The caller's `bodyRef` is the same node as `containerRef`, so both are set
   *  from one callback rather than the div carrying two refs. */
  const setContainer = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (bodyRef) bodyRef.current = node;
    },
    [bodyRef],
  );

  // The standard hydration gate — createPortal below needs a real document,
  // so the first client render has to be the one that flips this.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);

  /**
   * Mention anchors re-pointed at `/profil/{id}` BEFORE the markup is
   * injected. See `retargetMentionHrefs` above for why this cannot be done to
   * the DOM afterwards.
   */
  const safeHtml = useMemo(() => retargetMentionHrefs(html), [html]);
  /**
   * Memoised, and load-bearing rather than a render-count micro-optimisation.
   * React 19 re-applies `dangerouslySetInnerHTML` when the prop *object* is new,
   * even when `__html` is the identical string, so a fresh
   * `{ __html: safeHtml }` literal per render rebuilt this whole subtree on
   * every state change — every emoji tooltip, every hover card. Three things
   * broke quietly, all measured before this memo went in (OSO-512):
   *
   *   Focus. A keyboard user tabs to a mention; 400ms later the card opens, the
   *   subtree is rebuilt, the anchor they were on is replaced, and focus falls
   *   to <body>. Their next Tab starts from the top of the document.
   *
   *   The title effect below strips `title` from emoji imgs so the browser's
   *   native tooltip does not fight ours. Keyed on `[safeHtml, mounted]` it does
   *   not re-run — but the rebuild restored every `title` it had removed, which
   *   is the bug it was written to fix, arriving by another door.
   *
   *   Any DOM state inside the cooked HTML — a text selection, a <details> the
   *   reader opened — is discarded on an unrelated state change.
   */
  const cookedHtml = useMemo(() => ({ __html: safeHtml }), [safeHtml]);

  // Strip `title` from emoji imgs so the browser's native tooltip does not
  // fight ours. `mounted` became a dependency when the `mounted` flip re-applied
  // `dangerouslySetInnerHTML` and restored every `title` this had removed; the
  // `cookedHtml` memo above ended that, so the second run is a no-op over nodes
  // already stripped. It stays as insurance if that memo is ever lost again —
  // one idempotent querySelectorAll per post.
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current
      .querySelectorAll("img.emoji, img.theta-emoji")
      .forEach((img) => img.removeAttribute("title"));
  }, [safeHtml, mounted]);

  const handleMouseOver = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;

      const emojiImg =
        target instanceof HTMLImageElement &&
        (target.classList.contains("emoji") || target.classList.contains("theta-emoji"))
          ? target
          : null;

      if (emojiImg) {
        const shortcode = emojiImg.alt.replace(/^:|:$/g, "");
        if (shortcode) {
          const src = emojiImg.src;
          const rect = emojiImg.getBoundingClientRect();
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            setTooltip({ x: rect.left + rect.width / 2, y: rect.top, src, shortcode });
          }, 400);
        }
      } else {
        if (timerRef.current) clearTimeout(timerRef.current);
        setTooltip(null);
      }

      // Event delegation, same as the emoji handling above — one listener on
      // the container rather than one per mention, so it stays cheap on posts
      // with many mentions.
      const mentionAnchor = target.closest<HTMLAnchorElement>("a.mention");
      const userId = mentionAnchor ? mentionUserId(mentionAnchor) : null;

      if (userId === null) {
        if (mentionHover) {
          if (mentionOpenTimer.current) clearTimeout(mentionOpenTimer.current);
          mentionCloseTimer.current = setTimeout(() => setMentionHover(null), HOVER_CARD_CLOSE_DELAY);
        }
        return;
      }

      if (mentionCloseTimer.current) clearTimeout(mentionCloseTimer.current);
      if (mentionHover?.userId === userId) return;

      if (mentionOpenTimer.current) clearTimeout(mentionOpenTimer.current);
      const anchor = mentionAnchor as HTMLAnchorElement;
      mentionOpenTimer.current = setTimeout(() => {
        setMentionHover({ userId, coords: computeHoverCardPosition(anchor) });
      }, HOVER_CARD_OPEN_DELAY);
    },
    [mentionHover],
  );

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTooltip(null);

    if (mentionOpenTimer.current) clearTimeout(mentionOpenTimer.current);
    if (mentionHover) {
      mentionCloseTimer.current = setTimeout(() => setMentionHover(null), HOVER_CARD_CLOSE_DELAY);
    }
  }, [mentionHover]);

  /**
   * Keyboard equivalent of the mouseover/mouseleave handling above, so tabbing
   * to a mention (a real <a>) opens the same card a hover would, on the same
   * delay. Opening instantly is the defect OSO-458 fixed on the other trigger
   * and left standing here: tabbing across a paragraph of mentions flashed a
   * 300px card at every stop. A pass-through now opens nothing (OSO-512).
   */
  const handleFocus = useCallback((e: React.FocusEvent) => {
    // Focus arriving INSIDE the card is the Tab hand-off, not a new trigger:
    // it must not restart the timer or overwrite where Escape returns focus.
    if (mentionCardRef.current?.contains(e.target as Node)) return;

    const target = e.target as HTMLElement;
    const mentionAnchor = target.closest<HTMLAnchorElement>("a.mention");
    const userId = mentionAnchor ? mentionUserId(mentionAnchor) : null;
    if (userId === null) return;

    if (mentionCloseTimer.current) clearTimeout(mentionCloseTimer.current);
    if (mentionOpenTimer.current) clearTimeout(mentionOpenTimer.current);
    const anchor = mentionAnchor as HTMLAnchorElement;
    mentionOpenTimer.current = setTimeout(() => {
      setMentionHover({ userId, coords: computeHoverCardPosition(anchor) });
    }, HOVER_CARD_OPEN_DELAY);
  }, []);

  /**
   * Only a blur that leaves the mention-and-card pair counts as focus leaving.
   * The wrapper encloses the whole cooked body, so it also hears every ordinary
   * link in the post blur; those must neither cancel a pending open nor schedule
   * a close (OSO-516 — a mouse-opened card vanished whenever focus moved between
   * two plain links in the same post). Focus moving from the mention *into* the
   * card is the hand-off, not a departure, which is why `relatedTarget` is
   * checked rather than the blur's own target alone.
   */
  const handleBlur = useCallback((e: React.FocusEvent) => {
    const from = e.target as Element;
    if (!isMentionTrigger(from) && !mentionCardRef.current?.contains(from)) return;
    const next = e.relatedTarget as Node | null;
    if (next && mentionCardRef.current?.contains(next)) return;
    if (mentionOpenTimer.current) clearTimeout(mentionOpenTimer.current);
    mentionCloseTimer.current = setTimeout(() => setMentionHover(null), HOVER_CARD_CLOSE_DELAY);
  }, []);

  /**
   * A MENTION OF SOMEBODY WHO IS GONE STOPS BEING A LINK — OSO-620.
   *
   * The anchor is baked into the stored `body_html` at write time, so deleting
   * an account never rewrites the posts that named it: every one of them keeps
   * a live-looking link to a profile page that answers 404 (deliberately — see
   * `app/profil/[id]/page.tsx`, where a 200 saying "findes ikke" is refused
   * because it tells a crawler the URL is fine).
   *
   * We only learn the account is gone when the card looks it up, so this runs
   * on that answer: drop the `href` from every anchor for that id in this body,
   * and mute it, so it reads as a name rather than a promise. The name stays —
   * it is what the author wrote.
   *
   * WHAT THIS DOES NOT COVER: a reader who taps without ever hovering. The card
   * is delegated off `mouseover`/`focus`, so on a touch screen the lookup never
   * happens and the tap still reaches the 404. Closing that needs the SERVER to
   * say which mentions still resolve when it serializes the post — a wire
   * addition, and the durable fix.
   */
  const markMentionGone = useCallback((userId: number) => {
    const anchors = containerRef.current?.querySelectorAll<HTMLAnchorElement>(
      `a.mention[data-user-id="${userId}"]`,
    );
    anchors?.forEach((a) => {
      a.removeAttribute("href");
      /**
       * An `<a>` without an `href` is not focusable, and dropping it under a
       * keyboard user standing on the mention throws focus to `<body>` — the
       * blur then closes the card that had just opened, and `restoreMentionFocus`
       * has nothing to return to. `tabindex="0"` keeps the element in the tab
       * order; `aria-disabled` says why it no longer leads anywhere.
       */
      a.setAttribute("tabindex", "0");
      a.setAttribute("aria-disabled", "true");
      a.classList.add("mention-gone");
      // No `title`: a native tooltip would race the card for the same hover,
      // which is the reason the emoji pass a few lines above strips them.
    });
  }, []);

  const closeMentionCard = useCallback(() => setMentionHover(null), []);
  /**
   * Focus goes back to the mention the card is ANCHORED to, looked up by id
   * — not to whichever mention was focused last. Those differ whenever the
   * card was opened by the mouse on a different name than the one the reader
   * had tabbed to, and Escape then sent focus to the wrong person (OSO-516).
   * Looking the anchor up rather than holding the node is discussed in
   * `useHoverCardKeyboard`.
   */
  const anchoredMentionId = mentionHover?.userId ?? null;
  const restoreMentionFocus = useCallback(() => {
    if (anchoredMentionId == null) return;
    containerRef.current
      ?.querySelector<HTMLElement>(`a.mention[data-user-id="${anchoredMentionId}"]`)
      ?.focus();
  }, [anchoredMentionId]);
  const { onKeyDown: onMentionKeyDown } = useHoverCardKeyboard({
    open: mentionHover != null,
    cardRef: mentionCardRef,
    restoreFocus: restoreMentionFocus,
    close: closeMentionCard,
    isTrigger: isMentionTrigger,
  });

  const tooltipEl = tooltip ? (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        transform: "translateX(-50%) translateY(calc(-100% - 6px))",
      }}
    >
      <div className="bg-gray-900 rounded-lg shadow-xl px-3 py-2 flex flex-col items-center gap-1">
        <img src={tooltip.src} alt="" className="w-16 h-16 object-contain" />
        <span className="text-[11px] text-white font-medium">:{tooltip.shortcode}:</span>
      </div>
    </div>
  ) : null;

  return (
    /**
     * `display: contents` — a wrapper that is not a box.
     *
     * The keyboard handlers must sit on an ancestor of both the cooked HTML and
     * the portalled card *in the React tree*, because that is the tree React's
     * synthetic events bubble through: Tab going into the card originates on the
     * mention (inside the div), Shift+Tab coming back out originates inside the
     * portal. A handler on the div below catches the first and misses the
     * second, which is how the mention card came to announce `role="dialog"`
     * with no way back out (OSO-512).
     *
     * `display: contents` removes this element from layout, so the cooked-HTML
     * div keeps its own `className`, box and place in whatever container the
     * caller put it in. Mouse handlers stay on the div: they are about the HTML,
     * not the card.
     */
    <span
      style={{ display: "contents" }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onMentionKeyDown}
    >
      <div
        ref={setContainer}
        className={className}
        dangerouslySetInnerHTML={cookedHtml}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      />
      {mounted && tooltipEl && createPortal(tooltipEl, document.body)}
      {mounted &&
        mentionHover &&
        createPortal(
          <ProfileHoverCardPanel
            userId={mentionHover.userId}
            categoryId={categoryId}
            coords={mentionHover.coords}
            panelRef={mentionCardRef}
            onMouseEnter={() => {
              if (mentionCloseTimer.current) clearTimeout(mentionCloseTimer.current);
            }}
            onMouseLeave={() => {
              mentionCloseTimer.current = setTimeout(() => setMentionHover(null), HOVER_CARD_CLOSE_DELAY);
            }}
            onMissing={markMentionGone}
          />,
          document.body,
        )}
    </span>
  );
}
